import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { IModifiedPlan } from 'src/types/modifiedPlan.interface'
import { IMultiFamilyGridPlan } from 'src/types/multiFamily.grid.interface'
import { normalizeFlatPlanDataAndMapToModifiedPlan } from 'src/utils/dataNormalization.util'

import {
  MULTI_FAMILY_CONFIRM_PENDING_PLAN,
  PLAN_AUTO_EXTRACTED_PLANS,
  PLAN_CONFIRM_PENDING_PLAN,
} from '../../lib/api-routes'
import { instance } from '../../lib/axiosInstance'

export interface ExtractedPlansState {
  continuationToken?: string | null
  data: (IModifiedPlan | IMultiFamilyGridPlan)[]
  error: string | null
  isLoading: boolean
}

interface AutoExtractedPlansResponse {
  continuationToken?: string | null
  data: Array<Record<string, unknown>>
}

export interface PlansInitialState {
  allExtractedPlans: ExtractedPlansState
}

export const initialState: PlansInitialState = {
  allExtractedPlans: {
    data: [],
    error: null,
    isLoading: false,
  },
}

export const getAutoExtractedPlans = createAsyncThunk(
  'plans/getAutoExtractedPlans',
  async ({
    approverRegions,
    continuationToken,
  }: {
    approverRegions?: string[] | undefined
    continuationToken?: string | null
  }) => {
    return instance
      .get<AutoExtractedPlansResponse>(PLAN_AUTO_EXTRACTED_PLANS, {
        params: {
          approverRegions,
          continuationToken,
        },
      })
      .then((res) => (res?.data || []) as AutoExtractedPlansResponse)
  }
)

interface ConfirmPendingPlanPayload {
  extractedPlanId: string
  legacyCommunityId: string
  legacyElevation: string
  legacyPlan: string
  planClass: string
}

export const confirmPendingPlan = createAsyncThunk(
  'plans/confirmPendingPlan',
  async (params: ConfirmPendingPlanPayload) => {
    const url =
      params.planClass === 'Single-Family'
        ? PLAN_CONFIRM_PENDING_PLAN
        : MULTI_FAMILY_CONFIRM_PENDING_PLAN
    return instance.post(url, params).then((res) => res?.data || [])
  }
)

export const extractedPlans = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(getAutoExtractedPlans.pending, (state) => {
        state.allExtractedPlans.isLoading = true
        state.allExtractedPlans.error = null
      })
      .addCase(getAutoExtractedPlans.fulfilled, (state, action) => {
        state.allExtractedPlans.isLoading = false
        const mappedPlans = normalizeFlatPlanDataAndMapToModifiedPlan(
          action.payload.data
        )
        state.allExtractedPlans.data =
          state.allExtractedPlans.data.concat(mappedPlans)
        state.allExtractedPlans.continuationToken =
          action.payload.continuationToken
        state.allExtractedPlans.error = null
      })
      .addCase(getAutoExtractedPlans.rejected, (state, action) => {
        state.allExtractedPlans.isLoading = false
        state.allExtractedPlans.error =
          action.error.message ?? 'Error fetching auto extracted plans'
      })
  },
  initialState,
  name: 'extractedPlans',
  reducers: {
    removeExtractedPlanById: (state, action: { payload: string }) => {
      state.allExtractedPlans.data = state.allExtractedPlans.data.filter(
        (plan) => plan.id !== action.payload
      )
    },
    resetExtractedPlansData: (state) => {
      state.allExtractedPlans.data = []
      state.allExtractedPlans.isLoading = false
      state.allExtractedPlans.error = null
      state.allExtractedPlans.continuationToken = null
    },
  },
})

export const { removeExtractedPlanById, resetExtractedPlansData } =
  extractedPlans.actions
